import { useEffect, useState, useMemo} from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import KanbanBoard from '../components/kanban/KanbanBoard';
import PipelineEditorModal from '../components/kanban/PipelineEditorModal';
import {
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Settings,
  LayoutGrid,
  List,
  Search
} from 'lucide-react';

/* =========================
   LISTA DE LEADS (VISTA LISTA)
========================= */
function LeadsList({ leads }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left">Nombre</th>
            <th className="px-4 py-3 text-left">Email</th>
            <th className="px-4 py-3 text-left">Teléfono</th>
            <th className="px-4 py-3 text-left">Fuente</th>
            <th className="px-4 py-3 text-left">Origen</th>
            <th className="px-4 py-3 text-left">Embudo</th>
            <th className="px-4 py-3 text-left">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {leads.map(lead => (
            <tr
              key={lead.id}
              onClick={() => navigate(`/leads/${lead.id}`)}
              className="
                border-b last:border-b-0
                hover:bg-slate-50 cursor-pointer
                transition-colors
              "
            >
              <td className="px-4 py-3 font-medium">
                {lead.nombre || 'Sin nombre'}
              </td>

              <td className="px-4 py-3 text-slate-600">
                {lead.email || '—'}
              </td>

              <td className="px-4 py-3 text-slate-600">
                {lead.telefono || '—'}
              </td>

              <td className="px-4 py-3 text-slate-600 uppercase text-xs font-semibold">
                {lead.utm_source || '—'}
              </td>

              <td className="px-4 py-3 text-slate-600 text-xs font-bold uppercase">
                {lead.origen_nombre || 'Directo'}
              </td>

              <td className="px-4 py-3 text-slate-500">
                {lead.pipeline_nombre || '—'}
              </td>

              <td className="px-4 py-3 text-slate-400 text-xs">
                {lead.created_at
                  ? new Date(lead.created_at).toLocaleDateString()
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LeadsKanban() {
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPipelineEditor, setShowPipelineEditor] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [search, setSearch] = useState('');

  /* ===== PERMISOS ===== */
  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('usuario'));
    } catch {
      return null;
    }
  }, []);

  const canEditPipeline = ['admin', 'superadmin'].includes(usuario?.rol);

  /* ===== CARGA PIPELINE ===== */
  const cargar = async () => {
    setLoading(true);
    try {
      const res = await api.get('/leads/pipeline');
      setPipeline(res.data || []);
    } catch (error) {
      console.error('❌ Error cargando pipeline:', error);
      setPipeline([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  /* ===== NORMALIZAR LEADS ===== */
  const allLeads = useMemo(() => {
    return pipeline.flatMap(col =>
      (col.leads || []).map(lead => ({
        ...lead,
        pipeline_nombre: col.nombre
      }))
    );
  }, [pipeline]);

  /* ===== FILTRO DE BÚSQUEDA ===== */
  const filteredLeads = useMemo(() => {
    if (!search.trim()) return allLeads;

    const q = search.toLowerCase();
    return allLeads.filter(l =>
      l.nombre?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.telefono?.toLowerCase().includes(q)
    );
  }, [search, allLeads]);

  /* ===== PIPELINE FILTRADO (PARA KANBAN) ===== */
  const filteredPipeline = useMemo(() => {
    if (!search.trim()) return pipeline;

    const q = search.toLowerCase();
    return pipeline.map(col => ({
      ...col,
      leads: (col.leads || []).filter(l =>
        l.nombre?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.telefono?.toLowerCase().includes(q)
      )
    }));
  }, [search, pipeline]);

  /* ===== SKELETON ===== */
  if (loading) {
    return (
      <div className="p-8 max-w-[1600px] mx-auto animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded-lg mb-8" />
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-[70vh] bg-slate-100 rounded-2xl border border-slate-200"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] overflow-hidden">
      {/* ================= HEADER ================= */}
      <header className="px-8 py-6 bg-white border-b border-slate-100 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <TrendingUp size={16} strokeWidth={3} />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                CRM Sales Pipeline
              </span>
            </div>
            <h1 className="text-3xl font-serif text-slate-900 font-medium">
              Leads
            </h1>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* BUSCADOR */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar lead…"
                className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {/* TOGGLE VISTA */}
            <div className="flex bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1
                  ${viewMode === 'kanban' ? 'bg-white shadow' : 'text-slate-500'}`}
              >
                <LayoutGrid size={14} />
                Embudo
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1
                  ${viewMode === 'list' ? 'bg-white shadow' : 'text-slate-500'}`}
              >
                <List size={14} />
                Lista
              </button>
            </div>

            {/* ADMIN */}
            {canEditPipeline && (
              <button
                onClick={() => setShowPipelineEditor(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700"
              >
                <Settings size={14} />
                Embudos
              </button>
            )}

            <button
              onClick={cargar}
              className="flex items-center gap-2 border px-4 py-2 rounded-xl text-xs font-bold"
            >
              <RefreshCw size={14} />
              Actualizar
            </button>
          </div>
        </div>
      </header>

      {/* ================= MAIN ================= */}
      <main className="flex-1 overflow-auto bg-slate-50/50 p-8 custom-scrollbar">
        {pipeline.length === 0 ? (
          <div className="max-w-xl mx-auto mt-20 bg-white border rounded-3xl p-12 text-center">
            <AlertCircle size={40} className="mx-auto mb-4 text-yellow-500" />
            <p className="text-slate-600">No hay leads configurados</p>
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard pipeline={filteredPipeline} onChange={cargar} />
        ) : (
          <LeadsList leads={filteredLeads} />
        )}
      </main>

      {/* ================= MODAL ================= */}
      {showPipelineEditor && (
        <PipelineEditorModal
          pipeline={pipeline}
          onClose={() => setShowPipelineEditor(false)}
          onSave={(changes) => {
            console.log('Cambios de embudos:', changes);
            setShowPipelineEditor(false);
            cargar();
          }}
        />
      )}

      {/* ================= SCROLLBAR ================= */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}
